import type { ReactNode } from 'react'
import { AlertTriangle, Radio, RefreshCw } from 'lucide-react'
import { ApiContractError, ApiError } from '@/api/client'
import type { OrderImageItem } from '@/api/orders/types'
import { useOrders } from '@/features/orders/useOrders'

const cardImageHeights = [
  'aspect-[4/5]',
  'aspect-square',
  'aspect-[5/6]',
  'aspect-[4/3]',
  'aspect-[3/4]',
] as const

const placeholderColors = [
  'from-[#d9e9df] via-[#f5cfb5] to-[#e5c5d7]',
  'from-[#f6dfc1] via-[#f0b7a4] to-[#c7dce4]',
  'from-[#d9d4f1] via-[#f1c8c4] to-[#f3e3bd]',
  'from-[#cce4df] via-[#c5d9f1] to-[#ead7c6]',
  'from-[#f0d4d7] via-[#e8e4be] to-[#c8ded2]',
] as const

const dateFormatter = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
})

function formatDate(value: string | null): string {
  if (value === null) return 'Zeitpunkt nicht übermittelt'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : dateFormatter.format(date)
}

function formatPrice(value: number | null, currency: string): string | null {
  if (value === null) return null
  const safeCurrency = /^[A-Z]{3}$/.test(currency) ? currency : 'EUR'
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: safeCurrency,
  }).format(value)
}

function initials(title: string): string {
  return title.trim().slice(0, 1).toUpperCase() || 'P'
}

function OrderCardView({
  order,
  index,
}: {
  readonly order: OrderImageItem
  readonly index: number
}): ReactNode {
  const imageHeight = cardImageHeights[index % cardImageHeights.length] ?? cardImageHeights[0]
  const placeholderColor =
    placeholderColors[index % placeholderColors.length] ?? placeholderColors[0]
  const price = formatPrice(order.price, order.currency)

  return (
    <article className="mb-4 break-inside-avoid overflow-hidden rounded-[1.35rem] border border-slate-200/80 bg-white shadow-[0_10px_30px_-18px_rgba(15,23,42,0.4)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_18px_36px_-18px_rgba(15,23,42,0.45)] dark:border-slate-700 dark:bg-slate-800">
      <div className={`relative overflow-hidden ${imageHeight}`}>
        {order.imageUrl !== null ? (
          <img
            src={order.imageUrl}
            alt={order.productName}
            loading={index < 8 ? 'eager' : 'lazy'}
            className="h-full w-full object-cover"
          />
        ) : (
          <div
            aria-label={`Kein Produktbild für ${order.productName}`}
            className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${placeholderColor}`}
          >
            <span className="text-7xl font-black tracking-[-0.08em] text-white/75 drop-shadow-sm">
              {initials(order.productName)}
            </span>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/75 via-slate-950/20 to-transparent p-4 pt-12">
          <p className="text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-white/75">
            {order.channel}
          </p>
          <h2 className="mt-1 text-lg font-bold leading-tight text-white">{order.productName}</h2>
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 px-4 py-3.5">
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-slate-500 dark:text-slate-400">
            Bestellung {order.orderNumber} · Position {String(order.orderItemId)}
          </p>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
            {formatDate(order.orderedAt)}
          </p>
        </div>
        <div className="shrink-0 text-right">
          {price !== null && (
            <p className="text-sm font-bold tabular-nums text-slate-900 dark:text-slate-100">
              {price}
            </p>
          )}
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {`${String(order.quantity)} × Artikel · ${order.status}`}
          </p>
        </div>
      </div>
    </article>
  )
}

function OrderSkeleton({ index }: { readonly index: number }): ReactNode {
  const imageHeight = cardImageHeights[index % cardImageHeights.length] ?? cardImageHeights[0]
  return (
    <div className="mb-4 break-inside-avoid overflow-hidden rounded-[1.35rem] border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
      <div className={`${imageHeight} animate-pulse bg-slate-200 dark:bg-slate-700`} />
      <div className="space-y-2 p-4">
        <div className="h-3 w-2/3 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
      </div>
    </div>
  )
}

export function OrdersPage(): ReactNode {
  const { data, isLoading, isFetching, error, dataUpdatedAt, refetch } = useOrders()
  const orders = data
  const itemCount = orders.reduce((sum, order) => sum + order.quantity, 0)
  const channelCount = new Set(orders.map((order) => order.channel)).size
  const updatedAt =
    dataUpdatedAt > 0 ? formatDate(new Date(dataUpdatedAt).toISOString()) : 'Noch nicht'

  return (
    <div className="min-h-[calc(100vh-4.1rem)] bg-[#f6f4ef] dark:bg-slate-950">
      <div className="mx-auto max-w-[1440px] px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
        <section className="relative overflow-hidden rounded-[2rem] bg-[#173b3e] px-6 py-8 text-white shadow-[0_24px_60px_-32px_rgba(23,59,62,0.9)] sm:px-10 sm:py-10">
          <div className="pointer-events-none absolute -right-16 -top-24 h-72 w-72 rounded-full bg-[#efb6a1]/25 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-32 left-1/3 h-72 w-72 rounded-full bg-[#8dc4bd]/20 blur-3xl" />
          <div className="relative flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-[#d7eee7]">
                <Radio aria-hidden="true" size={15} strokeWidth={2.2} />
                Live-Verkäufe
              </div>
              <h1 className="max-w-xl text-4xl font-black tracking-[-0.04em] text-balance sm:text-5xl">
                Was gerade gekauft wird.
              </h1>
              <p className="mt-4 max-w-xl text-base leading-7 text-[#c3dad5]">
                Die neuesten Bestellungen als visuelles Signal für Marketing, Content und
                Produktideen. Diese Wand hält sich automatisch aktuell.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {[
                { label: 'Bestellungen', value: orders.length },
                { label: 'Artikel', value: itemCount },
                { label: 'Kanäle', value: channelCount || '–' },
              ].map((metric) => (
                <div
                  key={metric.label}
                  className="min-w-24 rounded-2xl border border-white/10 bg-white/10 px-3 py-3 backdrop-blur-sm sm:min-w-28 sm:px-4"
                >
                  <p className="text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-[#a9c9c3]">
                    {metric.label}
                  </p>
                  <p className="mt-1 text-2xl font-bold tabular-nums text-white">{metric.value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="flex flex-wrap items-center justify-between gap-3 py-6">
          <div>
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Die letzten 50 Bestellungen
            </p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Zuletzt aktualisiert: {updatedAt}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {isFetching ? 'Aktualisiere gerade …' : 'Nächste Aktualisierung automatisch'}
            </span>
            <button
              type="button"
              onClick={() => {
                void refetch()
              }}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#2d7672]/40 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <RefreshCw
                aria-hidden="true"
                size={15}
                strokeWidth={2}
                className={isFetching ? 'animate-spin' : undefined}
              />
              Jetzt aktualisieren
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-5 dark:border-red-900 dark:bg-red-950">
            <div className="flex items-start gap-2">
              <AlertTriangle aria-hidden="true" size={18} className="mt-0.5 shrink-0" />
              <p className="text-sm font-medium text-red-900 dark:text-red-100">
                {error instanceof ApiError || error instanceof ApiContractError
                  ? error.message
                  : 'Die Bestellungen konnten nicht geladen werden.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                void refetch()
              }}
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-red-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-800"
            >
              <RefreshCw aria-hidden="true" size={15} strokeWidth={2} />
              Erneut versuchen
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="columns-1 gap-4 sm:columns-2 xl:columns-4">
            {Array.from({ length: 8 }, (_, index) => (
              <OrderSkeleton key={index} index={index} />
            ))}
          </div>
        ) : orders.length > 0 ? (
          <div className="columns-1 gap-4 sm:columns-2 xl:columns-4">
            {orders.map((order, index) => (
              <OrderCardView
                key={`${String(order.orderItemId)}-${String(index)}`}
                order={order}
                index={index}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-white/70 px-6 py-20 text-center dark:border-slate-700 dark:bg-slate-900/60">
            <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">
              Noch keine Bestellungen für die Live-Wand.
            </p>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Sobald die API aktuelle Verkäufe liefert, erscheinen sie hier automatisch.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
